import type { SpaceMember } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// The org Representative — identity + naming (T-0173, spec §1/§5).
//
// The Representative is the org's front face: a per-org minted identity you chat
// with, which turns a half-baked idea into a Request the org can execute. It is
// **of** the canvas, never **on** it — a member of the org's coordination space, not
// a node on the chart. So we find it the way you find any member: in the roster.
//
// ## Naming (spec §5, non-negotiable)
//
// "Representative" is the concept word, because a representative speaks FOR the org —
// the noun itself carries the honesty law. "Assistant"/"copilot" would make it the
// *user's* agent, licensed to promise anything. Display name is
// "<Org>'s Representative"; tight chrome may shorten to "rep", never to anything else.
// ─────────────────────────────────────────────────────────────────────────────

/** The identity segment that marks a member as the org's Representative. */
export const REP_SEGMENT = 'rep';

/** The short form, for chrome too tight for the full display name (spec §5). */
export const REP_SHORT = 'rep';

/**
 * The trailing segment of a Tizok identity — the part that names the agent.
 *
 *   `@erick/rep:tizok.dev`   → `rep`
 *   `@erick.rep:tizok.ai`    → `rep`   (the org-cast URI shape)
 *   `@operator:tizok.dev`    → `operator`
 *   `rep`                    → `rep`   (a bare persona, already the segment)
 *
 * Both `/` and `.` separate an operator from the agent it owns, and `persona` is
 * sometimes already reduced server-side — so this normalizes every shape we can be
 * handed rather than assuming one.
 */
export function identitySegment(identity: string): string {
  const withoutDomain = identity.trim().replace(/^@/, '').split(':')[0] ?? '';
  const parts = withoutDomain.split(/[/.]/).filter(Boolean);
  return (parts[parts.length - 1] ?? '').toLowerCase();
}

/** Is this space member the org's Representative? */
export function isRepMember(member: Pick<SpaceMember, 'user_id' | 'persona'>): boolean {
  return (
    identitySegment(member.persona) === REP_SEGMENT ||
    identitySegment(member.user_id) === REP_SEGMENT
  );
}

/** The org's Representative among its space's members, if it has one yet. */
export function findRepMember<T extends Pick<SpaceMember, 'user_id' | 'persona'>>(
  members: readonly T[],
): T | undefined {
  return members.find(isRepMember);
}

/**
 * "Plant Care's Representative" — the display name (spec §5).
 *
 * An org whose name already ends in `s` takes a bare apostrophe ("Atlas' Representative"),
 * because the alternative reads as a typo on the one surface that is the org's face.
 */
export function repDisplayName(orgName: string | undefined): string {
  const name = orgName?.trim();
  if (!name) return 'The Representative';
  const possessive = /s$/i.test(name) ? `${name}’` : `${name}’s`;
  return `${possessive} Representative`;
}
